/**
 * CMS Self Signup — step-order, step id enum, and dependency map.
 *
 * These constants define the canonical 15-step KYC wizard order (Req 2.1),
 * a stable set of step identifiers used throughout the module (design StepId
 * typedef), and a dependency map describing which later steps depend on each
 * earlier identity field. The dependency map drives dependency-based
 * invalidation (Req 2.9 / Property 8): when an earlier identity field changes,
 * exactly the dependent later steps have their completion cleared.
 */

/**
 * @typedef {'phone'|'phoneOtp'|'email'|'emailOtp'|'authorization'|'pan'|
 *   'aadhaarInit'|'aadhaarStatus'|'aadhaarNames'|'panSimilarity'|'aadhaarSimilarity'|'selfie'|'bankList'|'bankVerify'|'kyc'} StepId
 */

/**
 * Stable step-id enum (design StepId typedef). Use these constants instead of
 * raw string literals so refactors stay type-safe and greppable.
 *
 * @type {Readonly<Record<string, StepId>>}
 */
export const STEP_IDS = Object.freeze({
    PHONE: 'phone', // CheckPhoneNumber
    PHONE_OTP: 'phoneOtp', // VerifyOTP
    EMAIL: 'email', // EmailVerification
    EMAIL_OTP: 'emailOtp', // OtpEmailVerification
    AUTHORIZATION: 'authorization', // GetAuthorization
    PAN: 'pan', // verifyPancard
    AADHAAR_INIT: 'aadhaarInit', // aadhaarDigilocker
    AADHAAR_STATUS: 'aadhaarStatus', // aadhaarDigilockerStatuscheck
    AADHAAR_NAMES: 'aadhaarNames', // verifyAadharNames
    PAN_SIMILARITY: 'panSimilarity', // textSimilarityCheckPancard (runs after verifyAadharNames)
    AADHAAR_SIMILARITY: 'aadhaarSimilarity', // textSimilarityCheckAadhar
    SELFIE: 'selfie', // imageSimilarityCheck (SELFIEAGENT)
    BANK_LIST: 'bankList', // fetchBankIserveu
    BANK_VERIFY: 'bankVerify', // verifyBank
    KYC: 'kyc' // UpdateKycInfo
});

/**
 * The 15 step ids in documented flow order (Req 2.1).
 *
 * CheckPhoneNumber → VerifyOTP → EmailVerification → OtpEmailVerification →
 * GetAuthorization → verifyPancard → aadhaarDigilocker →
 * aadhaarDigilockerStatuscheck → verifyAadharNames → textSimilarityCheckPancard →
 * textSimilarityCheckAadhar → imageSimilarityCheck → fetchBankIserveu →
 * verifyBank → UpdateKycInfo
 *
 * Note: textSimilarityCheckPancard (PAN_SIMILARITY) runs AFTER verifyAadharNames
 * (AADHAAR_NAMES) — it is no longer chained directly after verifyPancard.
 *
 * @type {ReadonlyArray<StepId>}
 */
export const STEP_ORDER = Object.freeze([
    STEP_IDS.PHONE,
    STEP_IDS.PHONE_OTP,
    STEP_IDS.EMAIL,
    STEP_IDS.EMAIL_OTP,
    STEP_IDS.AUTHORIZATION,
    STEP_IDS.PAN,
    STEP_IDS.AADHAAR_INIT,
    STEP_IDS.AADHAAR_STATUS,
    STEP_IDS.AADHAAR_NAMES,
    STEP_IDS.PAN_SIMILARITY,
    STEP_IDS.AADHAAR_SIMILARITY,
    STEP_IDS.SELFIE,
    STEP_IDS.BANK_LIST,
    STEP_IDS.BANK_VERIFY,
    STEP_IDS.KYC
]);

/**
 * Identity fields that later steps depend on. Mutating any of these fields must
 * invalidate the completion of the steps that consumed them (Req 2.9).
 *
 * @typedef {'phone'|'email'|'pan'|'aadhaar'|'selfie'|'bank'} IdentityField
 */
export const IDENTITY_FIELDS = Object.freeze({
    PHONE: 'phone',
    EMAIL: 'email',
    PAN: 'pan',
    AADHAAR: 'aadhaar',
    SELFIE: 'selfie',
    BANK: 'bank'
});

/**
 * Dependency map: for each earlier identity field, the list of later steps
 * whose positive Success_Indicator depends on that field's value. When the
 * field changes, exactly these dependent steps are cleared (Req 2.9,
 * Property 8) and no unrelated step is affected.
 *
 * The dependency chain follows the flow order: a field verified early is
 * consumed by its own verification step plus every downstream step that assumes
 * that identity remains fixed. Because the final KYC submission (`kyc`) bundles
 * every identity field into one payload, it depends on all of them.
 *
 * @type {Readonly<Record<IdentityField, ReadonlyArray<StepId>>>}
 */
export const STEP_DEPENDENCIES = Object.freeze({
    // Changing the phone number invalidates phone verification and everything after it.
    [IDENTITY_FIELDS.PHONE]: Object.freeze([
        STEP_IDS.PHONE,
        STEP_IDS.PHONE_OTP,
        STEP_IDS.EMAIL,
        STEP_IDS.EMAIL_OTP,
        STEP_IDS.AUTHORIZATION,
        STEP_IDS.PAN,
        STEP_IDS.AADHAAR_INIT,
        STEP_IDS.AADHAAR_STATUS,
        STEP_IDS.AADHAAR_NAMES,
        STEP_IDS.PAN_SIMILARITY,
        STEP_IDS.AADHAAR_SIMILARITY,
        STEP_IDS.SELFIE,
        STEP_IDS.BANK_LIST,
        STEP_IDS.BANK_VERIFY,
        STEP_IDS.KYC
    ]),
    // Changing the email invalidates email verification, authorization, and later steps.
    [IDENTITY_FIELDS.EMAIL]: Object.freeze([
        STEP_IDS.EMAIL,
        STEP_IDS.EMAIL_OTP,
        STEP_IDS.AUTHORIZATION,
        STEP_IDS.PAN,
        STEP_IDS.AADHAAR_INIT,
        STEP_IDS.AADHAAR_STATUS,
        STEP_IDS.AADHAAR_NAMES,
        STEP_IDS.PAN_SIMILARITY,
        STEP_IDS.AADHAAR_SIMILARITY,
        STEP_IDS.SELFIE,
        STEP_IDS.BANK_LIST,
        STEP_IDS.BANK_VERIFY,
        STEP_IDS.KYC
    ]),
    // Changing the PAN invalidates PAN verification and every later step. Because
    // PAN_SIMILARITY (textSimilarityCheckPancard) now runs after the aadhaar name
    // steps, it sits later in this list than AADHAAR_INIT/STATUS/NAMES.
    [IDENTITY_FIELDS.PAN]: Object.freeze([
        STEP_IDS.PAN,
        STEP_IDS.AADHAAR_INIT,
        STEP_IDS.AADHAAR_STATUS,
        STEP_IDS.AADHAAR_NAMES,
        STEP_IDS.PAN_SIMILARITY,
        STEP_IDS.AADHAAR_SIMILARITY,
        STEP_IDS.SELFIE,
        STEP_IDS.BANK_LIST,
        STEP_IDS.BANK_VERIFY,
        STEP_IDS.KYC
    ]),
    // Changing the Aadhaar invalidates the DigiLocker/name steps, the PAN + Aadhaar
    // similarity checks (which now run inside the aadhaar stage), and later steps.
    [IDENTITY_FIELDS.AADHAAR]: Object.freeze([
        STEP_IDS.AADHAAR_INIT,
        STEP_IDS.AADHAAR_STATUS,
        STEP_IDS.AADHAAR_NAMES,
        STEP_IDS.PAN_SIMILARITY,
        STEP_IDS.AADHAAR_SIMILARITY,
        STEP_IDS.SELFIE,
        STEP_IDS.BANK_LIST,
        STEP_IDS.BANK_VERIFY,
        STEP_IDS.KYC
    ]),
    // Changing the selfie invalidates the face-similarity step and later verification/submission steps.
    [IDENTITY_FIELDS.SELFIE]: Object.freeze([STEP_IDS.SELFIE, STEP_IDS.BANK_VERIFY, STEP_IDS.KYC]),
    // Changing the bank details invalidates bank verification and the final KYC step.
    [IDENTITY_FIELDS.BANK]: Object.freeze([STEP_IDS.BANK_VERIFY, STEP_IDS.KYC])
});
